import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  TrashIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  DocumentPlusIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { AnalysisResults } from "../components/AnalysisResults";
import { RiskBadge } from "../components/RiskBadge";
import { useDocumentReview } from "../hooks/useDocumentReview";
import { useCountries } from "@/hooks/useCountries";
import {
  formatFileSize,
  generateAnalysisReportPDF,
  downloadPDF,
} from "@/shared/core";
import {
  analyzeDocumentWithGroq,
  applyReviewCorrectionsToDocument,
} from "@/shared/ai";
import {
  updateReviewAnalysis,
  updateReviewStatus,
  createDocument,
  incrementUsage,
} from "@/shared/db";
import { checkLimit } from "@/shared/core";
import { useAuth } from "@/contexts/AuthContext";

export const ReviewDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const locale = i18n.language === "es" ? es : enUS;

  // Determine base path from current location
  const basePath = location.pathname.includes("/lawyer")
    ? "/dashboard/lawyer"
    : location.pathname.includes("/client")
      ? "/dashboard/client"
      : "";

  const { user } = useAuth();
  const { review, loading, error, deleteReview, refetch } =
    useDocumentReview(id);
  const { getCountryName } = useCountries();
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isApplyingCorrections, setIsApplyingCorrections] = useState(false);
  const [showReanalyzeModal, setShowReanalyzeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Detect stuck processing (>3 minutes)
  const isStuckProcessing =
    review?.analysis_status === "processing" &&
    review.updated_at &&
    Date.now() - new Date(review.updated_at).getTime() > 3 * 60 * 1000;

  const handleBack = () => {
    navigate(`${basePath}/document-review`);
  };

  const handleDeleteClick = () => {
    if (!review) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!review) return;

    setShowDeleteModal(false);
    const success = await deleteReview(review.id);
    if (success) {
      navigate(`${basePath}/document-review`);
    }
  };

  const handleReanalyzeClick = () => {
    if (!review || !review.original_content) {
      toast.error(t("documentReview.errors.noContent"));
      return;
    }
    setShowReanalyzeModal(true);
  };

  const handleStartAnalysis = async () => {
    if (!review || !review.original_content || !user) return;

    // Check plan limits
    const limitCheck = await checkLimit(user.id, "document_review");
    if (!limitCheck.allowed) {
      toast.error(t("documentReview.errors.limitReached"));
      return;
    }

    setIsAnalyzing(true);
    try {
      await updateReviewStatus(review.id, "processing");
      refetch();

      const analysisPromise = analyzeDocumentWithGroq({
        content: review.original_content,
        documentType: review.document_type,
        country: review.country,
        documentName: review.document_name,
        language: i18n.language === "en" ? "en" : "es",
      });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Analysis timed out")), 90000)
      );

      const analysisResult = await Promise.race([
        analysisPromise,
        timeoutPromise,
      ]);

      await updateReviewAnalysis({
        id: review.id,
        analysis_result: analysisResult,
        analysis_status: "completed",
        analyzed_at: new Date().toISOString(),
      });

      await incrementUsage(user.id, "document_review", 1);
      toast.success(t("documentReview.success.analyzed"));
      refetch();
    } catch (error: any) {
      console.error("Analysis error:", error);
      await updateReviewStatus(review.id, "failed");
      toast.error(
        error.message || t("documentReview.errors.analysisFailed")
      );
      refetch();
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCancelAndRetry = async () => {
    if (!review) return;
    await updateReviewStatus(review.id, "pending");
    toast.info(t("documentReview.cancelledRetry"));
    refetch();
  };

  const handleConfirmReanalyze = async () => {
    if (!review || !review.original_content) return;

    setShowReanalyzeModal(false);
    setIsReanalyzing(true);

    try {
      // Update status to processing
      await updateReviewStatus(review.id, "processing");
      refetch();

      // Perform analysis
      const analysisResult = await analyzeDocumentWithGroq({
        content: review.original_content,
        documentType: review.document_type,
        country: review.country,
        documentName: review.document_name,
        language: i18n.language === "en" ? "en" : "es",
      });

      // Update with results
      await updateReviewAnalysis({
        id: review.id,
        analysis_result: analysisResult,
        analysis_status: "completed",
        analyzed_at: new Date().toISOString(),
      });

      toast.success(t("documentReview.success.reanalyzed"));
      refetch();
    } catch (error: any) {
      console.error("Re-analysis error:", error);
      await updateReviewStatus(review.id, "failed");
      toast.error(error.message || t("documentReview.errors.reanalysisFailed"));
      refetch();
    } finally {
      setIsReanalyzing(false);
    }
  };

  const handleDownloadReport = () => {
    if (!review || !review.analysis_result) return;

    try {
      const blob = generateAnalysisReportPDF(
        {
          document_name: review.document_name,
          document_type: review.document_type,
          country: getCountryName(review.country),
          created_at: review.created_at,
          analysis_result: {
            overall_risk: review.analysis_result.overall_risk,
            summary: review.analysis_result.summary,
            risks: review.analysis_result.risks ?? [],
            omissions: review.analysis_result.omissions ?? [],
            recommendations: review.analysis_result.recommendations ?? [],
          },
        },
        {
          reportTitle: t("documentReview.analysisReport"),
          type: t("documentReview.table.type"),
          country: t("documentReview.table.country"),
          date: t("documentReview.table.date"),
          overallRisk: t("documentReview.overallRisk"),
          riskLevel: (level) =>
            t(`documentReview.riskLevels.${level}`, level),
          summary: t("documentReview.sections.summary"),
          risks: t("documentReview.sections.risks"),
          omissions: t("documentReview.sections.omissions"),
          recommendations: t("documentReview.sections.recommendations"),
        },
        { code: i18n.language }
      );
      downloadPDF(blob, `${review.document_name}_analysis.pdf`);
      toast.success(t("documentReview.success.reportDownloaded"));
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(t("documentReview.errors.downloadFailed"));
    }
  };

  const handleApplyCorrections = async () => {
    if (!review?.original_content || !review?.analysis_result || !user) return;

    setIsApplyingCorrections(true);
    try {
      const correctedContent = await applyReviewCorrectionsToDocument({
        originalContent: review.original_content,
        documentType: review.document_type,
        country: review.country,
        analysis: review.analysis_result,
      });

      const baseName = review.document_name?.replace(/\.[^/.]+$/, "") ?? "documento";
      const newDoc = await createDocument({
        user_id: user.id,
        title: `${baseName} - ${t("documentReview.applyCorrectionsSuffix")}`,
        document_type: review.document_type,
        country: review.country,
        content: correctedContent,
        status: "draft",
      });

      toast.success(t("documentReview.success.documentCreated"));
      navigate(`/document/${newDoc.id}?edit=1`);
    } catch (error: any) {
      console.error("Error applying corrections:", error);
      toast.error(
        error.message || t("documentReview.errors.applyCorrectionsFailed")
      );
    } finally {
      setIsApplyingCorrections(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-12 w-12 border-4 border-tiber-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            {t("common.error")}
          </h3>
          <p className="text-sm text-red-700">
            {error || t("documentReview.errors.notFound")}
          </p>
          <button
            onClick={handleBack}
            className="mt-4 text-sm text-red-700 hover:text-red-800 font-medium"
          >
            ← {t("common.goBack")}
          </button>
        </div>
      </div>
    );
  }

  const getStatusIcon = () => {
    switch (review.analysis_status) {
      case "completed":
        return <CheckCircleIcon className="h-6 w-6 text-atlantis-600" />;
      case "failed":
        return <XCircleIcon className="h-6 w-6 text-red-600" />;
      case "processing":
        return (
          <div className="h-6 w-6 border-2 border-tiber-600 border-t-transparent rounded-full animate-spin" />
        );
      default:
        return <ClockIcon className="h-6 w-6 text-titan" />;
    }
  };

  return (
    <div className="mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-tiber-600 hover:text-tiber-700 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          {t("common.back")}
        </button>

        <div className="bg-white rounded-lg border border-tiber-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-juridico mb-2">
                {review.document_name}
              </h1>
              <div className="flex items-center gap-4 text-sm font-light text-tiber-700">
                <span>{review.document_type}</span>
                <span>•</span>
                <span>{getCountryName(review.country)}</span>
                {review.file_size && (
                  <>
                    <span>•</span>
                    <span>{formatFileSize(review.file_size)}</span>
                  </>
                )}
                <span>•</span>
                <span>
                  {format(new Date(review.created_at), "PPP", { locale })}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {review.analysis_status === "completed" && (
                <>
                  <button
                    onClick={handleApplyCorrections}
                    disabled={isApplyingCorrections}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-dorado text-juridico rounded-lg hover:bg-dorado-dark transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    <DocumentPlusIcon
                      className={`h-5 w-5 ${isApplyingCorrections ? "animate-pulse" : ""}`}
                    />
                    {isApplyingCorrections
                      ? t("documentReview.applyCorrectionsCreating")
                      : t("documentReview.applyCorrections")}
                  </button>
                  <button
                    onClick={handleDownloadReport}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-tiber-600 text-white rounded-lg hover:bg-tiber-700 transition-colors cursor-pointer"
                  >
                    <DocumentArrowDownIcon className="h-5 w-5" />
                    {t("common.download")}
                  </button>
                  <button
                    onClick={handleReanalyzeClick}
                    disabled={isReanalyzing}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-tiber-800 text-white rounded-lg hover:bg-tiber-900 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowPathIcon
                      className={`h-5 w-5 ${isReanalyzing ? "animate-spin" : ""}`}
                    />
                    {isReanalyzing
                      ? t("documentReview.reanalyzing")
                      : t("documentReview.reanalyze")}
                  </button>
                </>
              )}
              {review.analysis_status === "pending" &&
                review.original_content && (
                  <button
                    onClick={handleStartAnalysis}
                    disabled={isAnalyzing}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-tiber-600 text-white rounded-lg hover:bg-tiber-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlayIcon
                      className={`h-5 w-5 ${isAnalyzing ? "animate-pulse" : ""}`}
                    />
                    {isAnalyzing
                      ? t("documentReview.startingAnalysis")
                      : t("documentReview.startAnalysis")}
                  </button>
                )}
              {review.analysis_status === "failed" && (
                <button
                  onClick={handleReanalyzeClick}
                  disabled={isReanalyzing}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-tiber-600 text-white rounded-lg hover:bg-tiber-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowPathIcon
                    className={`h-5 w-5 ${isReanalyzing ? "animate-spin" : ""}`}
                  />
                  {isReanalyzing
                    ? t("documentReview.reanalyzing")
                    : t("documentReview.retryAnalysis")}
                </button>
              )}
              {isStuckProcessing && (
                <button
                  onClick={handleCancelAndRetry}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors cursor-pointer"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                  {t("documentReview.cancelAndRetry")}
                </button>
              )}
              <button
                onClick={handleDeleteClick}
                className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
              >
                <TrashIcon className="h-5 w-5" />
                {t("common.delete")}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <span className="text-sm font-medium text-juridico">
                {t(`documentReview.status.${review.analysis_status}`)}
              </span>
            </div>
            {review.analysis_status === "completed" &&
              review.analysis_result?.overall_risk && (
                <>
                  <span className="text-tiber-300">|</span>
                  <RiskBadge risk={review.analysis_result.overall_risk} />
                </>
              )}
          </div>
        </div>
      </div>

      {/* Analysis Results */}
      {review.analysis_status === "completed" && review.analysis_result ? (
        <AnalysisResults
          analysis={review.analysis_result}
          onDownloadReport={handleDownloadReport}
        />
      ) : review.analysis_status === "processing" ? (
        <div className="bg-tiber-50 border border-tiber-200 rounded-lg p-8 text-center">
          <div className="h-12 w-12 border-4 border-tiber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-tiber-900 mb-2">
            {t("documentReview.analyzing")}
          </h3>
          <p className="text-sm text-tiber-700">
            {t("documentReview.analyzingDescription")}
          </p>
          {isStuckProcessing && (
            <div className="mt-4">
              <p className="text-sm text-tiber-600 mb-2">
                {t("documentReview.stuckProcessingHint")}
              </p>
              <button
                onClick={handleCancelAndRetry}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors cursor-pointer"
              >
                <ArrowPathIcon className="h-5 w-5" />
                {t("documentReview.cancelAndRetry")}
              </button>
            </div>
          )}
        </div>
      ) : review.analysis_status === "failed" ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <XCircleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            {t("documentReview.status.failed")}
          </h3>
          <p className="text-sm text-red-700">
            {t("documentReview.analysisFailed")}
          </p>
        </div>
      ) : (
        <div className="bg-romance-50 border border-tiber-200 rounded-lg p-8 text-center">
          <ClockIcon className="h-12 w-12 text-titan mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-juridico mb-2">
            {t("documentReview.status.pending")}
          </h3>
          <p className="text-sm text-tiber-700">
            {t("documentReview.pendingDescription")}
          </p>
          {review.original_content && (
            <button
              onClick={handleStartAnalysis}
              disabled={isAnalyzing}
              className="mt-4 inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-tiber-600 text-white rounded-lg hover:bg-tiber-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayIcon
                className={`h-5 w-5 ${isAnalyzing ? "animate-pulse" : ""}`}
              />
              {isAnalyzing
                ? t("documentReview.startingAnalysis")
                : t("documentReview.startAnalysis")}
            </button>
          )}
        </div>
      )}

      {/* Re-analyze Confirmation Modal */}
      {showReanalyzeModal && review && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-juridico/60 backdrop-blur-sm transition-opacity"
              onClick={() => setShowReanalyzeModal(false)}
            />

            {/* Modal */}
            <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-juridico mb-2">
                  {t("documentReview.reanalyze")}
                </h3>
                <p className="text-sm text-tiber-700">
                  {t("documentReview.confirmReanalyze", {
                    name: review.document_name,
                  })}
                </p>
              </div>

              <div className="bg-tiber-500/10 border-2 border-tiber-500/30 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-2">
                  <svg
                    className="h-5 w-5 text-tiber-500 mt-0.5 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="text-sm text-juridico">
                    <p className="font-medium mb-1">
                      {t("documentReview.reanalyzeInfo")}
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>{t("documentReview.reanalyzePoint1")}</li>
                      <li>{t("documentReview.reanalyzePoint2")}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowReanalyzeModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-tiber-800 bg-romance-50 rounded-lg hover:bg-tiber-100 transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleConfirmReanalyze}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-tiber-600 rounded-lg hover:bg-tiber-700 transition-colors"
                >
                  {t("documentReview.reanalyze")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && review && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-juridico/60 backdrop-blur-sm transition-opacity"
              onClick={() => setShowDeleteModal(false)}
            />

            {/* Modal */}
            <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="shrink-0">
                    <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                      <svg
                        className="h-6 w-6 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-juridico">
                      {t("documentReview.deleteTitle")}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-tiber-700">
                  {t("documentReview.confirmDelete", {
                    name: review.document_name,
                  })}
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800">
                  <strong className="font-semibold">
                    {t("documentReview.deleteWarning")}
                  </strong>{" "}
                  {t("documentReview.deleteWarningDesc")}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-tiber-800 bg-romance-50 rounded-lg hover:bg-tiber-100 transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t("common.delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
